import { z } from 'zod';
import {
  isoDateSchema,
  longTextSchema,
  moneySchema,
  nonNegativeMoneySchema,
  positiveMoneySchema,
  shortTextSchema,
  uuidSchema,
} from '../primitives.js';
import { BIKE_OWNERSHIPS, PAYMENT_METHODS, VEHICLE_CATEGORIES } from '../enums.js';

/**
 * One remittance week, for every rider who belongs on it.
 *
 * The collection sheet used to be a list of the rows an accrual had posted, so
 * a week nobody had accrued showed nobody — "Nothing accrued for this week" —
 * and the only way to see a rider was to post money first. A week is now a
 * date range from the business's week boundaries, and every rider who belongs
 * on it is shown whether or not it has been posted:
 *
 *   posted       a `rider_remittances` row exists: the figure is a fact
 *   not accrued  the rider owes this week but nothing has been posted: the
 *                figure is what posting WOULD charge, and says it is provisional
 *   not required the rider is on the week but owes nothing (exempt, or
 *                riding a bike they own)
 *
 * Who is on a week at all is the Bike Register's answer: a rider assigned a
 * registered bike for it. A rider without one is not listed.
 *
 * Viewing writes nothing. Posting is a separate, permissioned action.
 *
 * Two kinds of "paid this week" are kept apart on purpose: money ALLOCATED to
 * this week (whenever it arrived) and money RECEIVED during this week (whatever
 * week it paid). They are different numbers, and the cash in the drawer is the
 * second one.
 */

// ── Statuses ────────────────────────────────────────────────────────────────

export const REMITTANCE_WEEK_STATUSES = [
  'unpaid',
  'part_paid',
  'paid',
  'waived',
  'not_accrued',
  'not_required',
] as const;
export type RemittanceWeekStatus = (typeof REMITTANCE_WEEK_STATUSES)[number];

export const REMITTANCE_WEEK_STATUS_LABELS: Record<RemittanceWeekStatus, string> = {
  unpaid: 'Unpaid',
  part_paid: 'Part Paid',
  paid: 'Paid / Complete',
  waived: 'Waived',
  not_accrued: 'Not Accrued',
  not_required: 'No Remittance Required',
};

/**
 * Where one rider's week stands.
 *
 * Paid means the obligation was settled BY PAYMENT. A week settled by
 * forgiving the balance — a waiver, or the old "mark paid" that covered a
 * shortfall — is Waived, with the reason shown, never Paid: a quarter of
 * "paid" weeks that were really write-offs is the thing nobody can see later.
 *
 * Pure, and `today` is not read from the clock, so the boundary is testable.
 */
export function remittanceWeekStatus(input: {
  posted: boolean;
  required: boolean;
  /** The posted (or provisional) amount due, after adjustments. */
  amountDue: number;
  paid: number;
  /** A stored waiver, or a settlement that forgave a shortfall. */
  waived: boolean;
  /** Marked paid before the payment ledger existed: no payment rows. */
  legacySettled: boolean;
}): RemittanceWeekStatus {
  if (!input.posted) return input.required ? 'not_accrued' : 'not_required';
  if (input.waived) return 'waived';
  if (input.legacySettled) return 'paid';
  if (input.amountDue <= 0) return 'not_required';
  if (input.paid >= input.amountDue) return 'paid';
  return input.paid > 0 ? 'part_paid' : 'unpaid';
}

/** Overdue is a badge beside the status, not a status: Part Paid can be late. */
export function remittanceWeekOverdue(input: {
  posted: boolean;
  remaining: number;
  overdueAfter: string;
  today: string;
}): boolean {
  return input.posted && input.remaining > 0 && input.today > input.overdueAfter;
}

// ── Week arithmetic ────────────────────────────────────────────────────────

const DAY = 86_400_000;

function shift(isoDate: string, days: number): string {
  return new Date(Date.parse(`${isoDate}T00:00:00Z`) + days * DAY).toISOString().slice(0, 10);
}

/** The first day of the week containing `isoDate`, for a week starting on `weekStartsOn` (0 = Sunday). */
export function remittanceWeekStart(isoDate: string, weekStartsOn: number): string {
  const weekday = new Date(`${isoDate}T00:00:00Z`).getUTCDay();
  const back = (weekday - weekStartsOn + 7) % 7;
  return shift(isoDate, -back);
}

export function remittanceWeekEnd(startsOn: string): string {
  return shift(startsOn, 6);
}

export function shiftRemittanceWeek(startsOn: string, weeks: number): string {
  return shift(startsOn, weeks * 7);
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** "12 – 18 Jul 2026", or "28 Jun – 4 Jul 2026" across a month. */
export function remittanceWeekLabel(startsOn: string, endsOn: string): string {
  const [sy, sm, sd] = startsOn.split('-').map(Number) as [number, number, number];
  const [ey, em, ed] = endsOn.split('-').map(Number) as [number, number, number];
  if (sy === ey && sm === em) return `${sd} – ${ed} ${MONTHS[em - 1]} ${ey}`;
  if (sy === ey) return `${sd} ${MONTHS[sm - 1]} – ${ed} ${MONTHS[em - 1]} ${ey}`;
  return `${sd} ${MONTHS[sm - 1]} ${sy} – ${ed} ${MONTHS[em - 1]} ${ey}`;
}

// ── How a figure was arrived at ────────────────────────────────────────────

export const REMITTANCE_RULES = [
  'agreement_rider',
  'agreement_vehicle',
  'rider_rate',
  'ride_tier_policy',
  'category_state',
  'category',
  'workspace',
  'exempt',
  // The rider owns the vehicle they ride: no weekly bike remittance.
  'rider_owned',
  'manual',
  'legacy',
] as const;
export type RemittanceRule = (typeof REMITTANCE_RULES)[number];

export const remittanceCalculationSchema = z.object({
  rule: z.enum(REMITTANCE_RULES),
  amount: moneySchema,
  /** Completed rides counted for the week, when the rule depends on them. */
  rides: z.number().int().nullable(),
  /** The ride-tier policy's terms, when it applied. */
  policy: z
    .object({
      key: z.string(),
      threshold: z.number().int(),
      atOrAbove: moneySchema,
      below: moneySchema,
      effectiveFrom: z.string(),
    })
    .nullable(),
  /** In words: "10 completed rides ≥ 10 → ₦15,000". */
  description: z.string(),
  /**
   * `payment` when the week was charged because a payment was recorded on it
   * before anyone posted it; `individual` when somebody charged this one
   * rider by hand before the week was posted. Either way posting the week
   * still charges everyone else.
   */
  origin: z.enum(['posting', 'payment', 'individual']).optional(),
  /**
   * Charged on an open ride-tier week, from the rides so far. Repriced as more
   * rides complete (never below what has been paid) and fixed when posted.
   */
  provisional: z.boolean().optional(),
});
export type RemittanceCalculation = z.infer<typeof remittanceCalculationSchema>;

// ── The sheet ──────────────────────────────────────────────────────────────

export const remittanceWeekRefSchema = z.object({
  startsOn: z.string(),
  endsOn: z.string(),
  label: z.string(),
  /** The stored period covering this week, when one exists. */
  periodId: uuidSchema.nullable(),
  /** True once any rider has a posted row for it. */
  posted: z.boolean(),
});
export type RemittanceWeekRef = z.infer<typeof remittanceWeekRefSchema>;

export const remittanceWeekRowSchema = z.object({
  riderId: uuidSchema,
  riderName: z.string(),
  riderPhone: z.string().nullable(),
  riderStatus: z.string(),
  branchId: uuidSchema.nullable(),
  branchName: z.string().nullable(),
  countryCode: z.string().nullable(),
  stateCode: z.string().nullable(),
  vehicleId: uuidSchema.nullable(),
  vehicleRegistration: z.string().nullable(),
  vehicleCategory: z.enum(VEHICLE_CATEGORIES).nullable(),
  /**
   * Assigned a registered bike for this week in the Bike Register. False only
   * on a charge written before the rider lost it, or before the rule: kept as
   * history — what it charged can still be collected, but no new charge can
   * be added for a rider without a bike.
   */
  bikeEligible: z.boolean(),

  /** Completed deliveries in the week, counted now. */
  completedRides: z.number().int(),
  /** The ride count stored when the week was posted, where it was. */
  postedRides: z.number().int().nullable(),

  remittanceId: uuidSchema.nullable(),
  posted: z.boolean(),
  /** True when `amountDue` is what posting would charge, not a posted fact. */
  provisional: z.boolean(),
  amountDue: moneySchema,
  /** What the week charged when first posted, where it has changed since. */
  originalAmount: moneySchema.nullable(),
  adjusted: z.boolean(),
  calculation: remittanceCalculationSchema.nullable(),

  amountPaid: moneySchema,
  remaining: moneySchema,
  /** Forgiven rather than paid: a waiver, or a shortfall settled with one. */
  waivedAmount: moneySchema,
  status: z.enum(REMITTANCE_WEEK_STATUSES),
  /**
   * How a week with nothing left to pay got there.
   *
   *   payment           paid in full through the ledger
   *   legacy            marked paid before payments were recorded one by one
   *   waiver            forgiven outright — not revenue
   *   shortfall_waived  "settled" with less than the charge; the rest forgiven.
   *                     Still revenue (it was accrued), and the forgiven part
   *                     is shown, never called paid.
   */
  settledBy: z.enum(['payment', 'legacy', 'waiver', 'shortfall_waived']).nullable(),
  overdue: z.boolean(),
  /** Why a week is settled without full payment, in the words recorded. */
  settlementReason: z.string().nullable(),
  dueOn: z.string(),
  overdueAfter: z.string(),
  daysLate: z.number().int(),
  latestPaymentOn: z.string().nullable(),

  /** Outstanding on this rider's earlier weeks — kept apart from this week. */
  arrearsBefore: moneySchema,
  /** Things somebody should look at: an agreement the rule disagrees with, a posted figure the rides no longer support. */
  /**
   * Who the week's money belongs to. The investor's entitlement is fixed on the
   * week when it is charged and never exceeds the amount due; the company's is
   * the rest. Collected and outstanding always add up to the week's paid and
   * remaining. `fixed` is false for a week not yet charged (a projection) and
   * for a week charged before ownership was tracked (all company).
   */
  split: z.object({
    ownership: z.enum(BIKE_OWNERSHIPS).nullable(),
    investorId: z.string().nullable(),
    investorName: z.string().nullable(),
    fixed: z.boolean(),
    legacy: z.boolean(),
    investorEntitlement: moneySchema,
    investorCollected: moneySchema,
    investorOutstanding: moneySchema,
    companyEntitlement: moneySchema,
    companyCollected: moneySchema,
    companyOutstanding: moneySchema,
    /**
     * When the bike changed riders in the week, the investor's weekly share is
     * divided by days held: this rider's days, and the days it was divided
     * over. Null when it was not divided.
     */
    heldDays: z.number().int().nullable().optional(),
    sharedDays: z.number().int().nullable().optional(),
    /** Why the investor's share on this week needs a person to check it. */
    review: z.string().nullable().optional(),
  }),
  conflicts: z.array(z.string()),
});
export type RemittanceWeekRow = z.infer<typeof remittanceWeekRowSchema>;

export const remittanceWeekPolicySchema = z.object({
  key: z.string(),
  label: z.string(),
  threshold: z.number().int(),
  atOrAbove: moneySchema,
  below: moneySchema,
  effectiveFrom: z.string(),
  /** Whether the selected week is on or after the effective date. */
  applies: z.boolean(),
});
export type RemittanceWeekPolicy = z.infer<typeof remittanceWeekPolicySchema>;

export const remittanceWeekSheetSchema = z.object({
  week: remittanceWeekRefSchema.extend({
    /** True until the week has ended in the business's timezone. */
    open: z.boolean(),
    dueOn: z.string(),
    overdueAfter: z.string(),
    revenueRecognitionDate: z.string(),
  }),
  weeks: z.array(remittanceWeekRefSchema),
  weekStartsOn: z.number().int(),
  graceDays: z.number().int(),
  /** The business default for an investor's weekly share. */
  investorWeeklyEntitlement: moneySchema,
  today: z.string(),
  currencyCode: z.string(),
  policy: remittanceWeekPolicySchema.nullable(),
  rows: z.array(remittanceWeekRowSchema),
  totals: z.object({
    riders: z.number().int(),
    /** Posted, excluding waived weeks — the revenue figure. */
    accrued: moneySchema,
    /** What posting the not-yet-posted weeks would add. Never revenue. */
    provisional: moneySchema,
    /** Allocated to this week, whenever it was received. */
    collected: moneySchema,
    /** Received during this week's dates, whichever week it paid. */
    receivedInWeek: moneySchema,
    outstanding: moneySchema,
    /** Waived weeks, not in `accrued`. */
    waived: moneySchema,
    /** Shortfalls forgiven when a week was settled — inside `accrued`. */
    settledWithWaiver: moneySchema,
    /** Cleared by the owner from weeks still partly owed — inside `accrued`. */
    clearedPart: moneySchema,
    /** Net change from adjustments to posted figures. */
    adjustments: moneySchema,
    overdueRiders: z.number().int(),
    notAccruedRiders: z.number().int(),
    /** Outstanding on earlier weeks, for the riders shown. */
    arrears: moneySchema,
    /** `accrued`, `collected` and `outstanding`, each divided investor / company. */
    investorEntitlement: moneySchema,
    investorCollected: moneySchema,
    investorOutstanding: moneySchema,
    companyEntitlement: moneySchema,
    companyCollected: moneySchema,
    companyOutstanding: moneySchema,
  }),
});
export type RemittanceWeekSheet = z.infer<typeof remittanceWeekSheetSchema>;

export const remittanceWeekQuerySchema = z.object({
  /** Any date in the week; omitted means this week. */
  startsOn: isoDateSchema.optional(),
  periodId: uuidSchema.optional(),
  category: z.enum(VEHICLE_CATEGORIES).optional(),
  country: z.string().trim().min(2).max(2).optional(),
  /** A state code, or `__unset__` for riders whose location names none. */
  state: z.string().trim().min(1).max(40).optional(),
  branchId: uuidSchema.optional(),
  /** Who owns the vehicle; the investor, when one. */
  ownership: z.enum(BIKE_OWNERSHIPS).optional(),
  investorId: uuidSchema.optional(),
});
export type RemittanceWeekQuery = z.infer<typeof remittanceWeekQuerySchema>;

// ── One rider's week, in full ──────────────────────────────────────────────

export const remittanceAllocationViewSchema = z.object({
  id: uuidSchema,
  receiptId: uuidSchema.nullable(),
  amount: moneySchema,
  /** The receipt's whole amount, when this is one share of a split payment. */
  receiptAmount: moneySchema.nullable(),
  /** This allocation's two shares, fixed when it was recorded. */
  investorAmount: moneySchema,
  companyAmount: moneySchema,
  paidOn: z.string(),
  method: z.string().nullable(),
  accountName: z.string().nullable(),
  reference: z.string().nullable(),
  note: z.string().nullable(),
  recordedBy: z.string().nullable(),
  createdAt: z.string(),
  voided: z.boolean(),
  voidReason: z.string().nullable(),
  voidedBy: z.string().nullable(),
  voidedAt: z.string().nullable(),
  /**
   * Whether the person asking may void it: anyone who manages remittance, or
   * the person who recorded it, on the day they recorded it.
   */
  mayVoid: z.boolean(),
});
export type RemittanceAllocationView = z.infer<typeof remittanceAllocationViewSchema>;

export const remittanceAdjustmentViewSchema = z.object({
  id: uuidSchema,
  kind: z.string(),
  amountBefore: moneySchema.nullable(),
  amountAfter: moneySchema.nullable(),
  reason: z.string().nullable(),
  /** For a change of due date: the dates either side of it. */
  dueOnBefore: z.string().nullable(),
  dueOnAfter: z.string().nullable(),
  recordedBy: z.string().nullable(),
  createdAt: z.string(),
});
export type RemittanceAdjustmentView = z.infer<typeof remittanceAdjustmentViewSchema>;

export const remittanceWeekDeliverySchema = z.object({
  id: uuidSchema,
  reference: z.string().nullable(),
  completedOn: z.string(),
  source: z.string(),
  fee: moneySchema,
  riderPayout: moneySchema,
  status: z.string(),
});

export const remittanceAgreementViewSchema = z.object({
  id: uuidSchema,
  riderId: uuidSchema.nullable(),
  bikeId: uuidSchema.nullable(),
  amount: moneySchema,
  effectiveFrom: z.string(),
  effectiveTo: z.string().nullable(),
  reason: z.string(),
  createdAt: z.string(),
});
export type RemittanceAgreementView = z.infer<typeof remittanceAgreementViewSchema>;

export const remittanceWeekDetailSchema = z.object({
  week: remittanceWeekSheetSchema.shape.week,
  graceDays: z.number().int(),
  currencyCode: z.string(),
  row: remittanceWeekRowSchema,
  deliveries: z.array(remittanceWeekDeliverySchema),
  allocations: z.array(remittanceAllocationViewSchema),
  adjustments: z.array(remittanceAdjustmentViewSchema),
  agreements: z.array(remittanceAgreementViewSchema),
  /** Every posted week with money still owed, oldest first. */
  outstandingWeeks: z.array(
    z.object({
      remittanceId: uuidSchema,
      startsOn: z.string(),
      endsOn: z.string(),
      label: z.string(),
      remaining: moneySchema,
    }),
  ),
  totalOutstanding: moneySchema,
});
export type RemittanceWeekDetail = z.infer<typeof remittanceWeekDetailSchema>;

// ── Writes ─────────────────────────────────────────────────────────────────

/**
 * Money a rider handed over, and which weeks it pays.
 *
 * `allocations` must total `amount` exactly: the money can be split across
 * weeks but never counted twice, and none of it can go unaccounted for.
 * `idempotencyKey` is one per form: a retried submission returns the receipt
 * already written instead of a second one.
 */
export const remittanceReceiptInputSchema = z.object({
  riderId: uuidSchema,
  amount: positiveMoneySchema,
  paidOn: isoDateSchema,
  method: z.enum(PAYMENT_METHODS),
  accountId: uuidSchema.optional(),
  reference: shortTextSchema.optional(),
  note: longTextSchema.optional(),
  idempotencyKey: z.string().trim().min(8).max(100),
  /**
   * Which weeks the money pays, each by its charge, or — for a week nobody has
   * posted — by the day it starts, which charges that week for this rider at
   * the price posting would use and records the payment against it, together.
   */
  allocations: z
    .array(
      z
        .object({
          remittanceId: uuidSchema.optional(),
          weekStartsOn: isoDateSchema.optional(),
          amount: positiveMoneySchema,
        })
        .refine((a) => (a.remittanceId === undefined) !== (a.weekStartsOn === undefined), {
          message: 'Name the week by its charge or by the day it starts, not both',
        }),
    )
    .min(1)
    .max(52),
  /**
   * The same rider, amount, date and reference as a receipt already on file
   * is refused as a likely duplicate unless this says it is deliberate.
   */
  confirmDuplicate: z.boolean().optional(),
});
export type RemittanceReceiptInput = z.infer<typeof remittanceReceiptInputSchema>;

/**
 * Correct a payment: the wrong one is voided and the right one recorded, in
 * one step. Nothing is edited — both stay on the record, the first marked
 * void with the reason.
 */
export const remittanceCorrectionSchema = z.object({
  reason: z.string().trim().min(3).max(500),
  payment: remittanceReceiptInputSchema,
});
export type RemittanceCorrectionInput = z.infer<typeof remittanceCorrectionSchema>;

export const remittanceVoidSchema = z.object({
  reason: z.string().trim().min(3).max(500),
});

export const remittanceAmountChangeSchema = z.object({
  amount: nonNegativeMoneySchema,
  reason: z.string().trim().min(3).max(500),
});
export type RemittanceAmountChange = z.infer<typeof remittanceAmountChangeSchema>;

export const remittanceAgreementInputSchema = z
  .object({
    riderId: uuidSchema.optional(),
    bikeId: uuidSchema.optional(),
    amount: nonNegativeMoneySchema,
    effectiveFrom: isoDateSchema,
    reason: z.string().trim().min(3).max(500),
  })
  .refine((v) => (v.riderId ? 1 : 0) + (v.bikeId ? 1 : 0) === 1, {
    message: 'Name a rider or a vehicle, not both',
    path: ['riderId'],
  });
export type RemittanceAgreementInput = z.infer<typeof remittanceAgreementInputSchema>;

export const remittancePostWeekSchema = z.object({
  startsOn: isoDateSchema,
});

export const remittanceSettingsSchema = z
  .object({
    weekStartsOn: z.number().int().min(0).max(6).optional(),
    /** Days after a week's due date before it is overdue. */
    graceDays: z.number().int().min(0).max(60).optional(),
    /**
     * An investor's weekly share when their bike has no agreement of its own.
     * Applies to weeks charged from now; a week already charged keeps its split.
     */
    investorWeeklyEntitlement: nonNegativeMoneySchema.optional(),
  })
  .refine((v) => v.weekStartsOn !== undefined || v.graceDays !== undefined || v.investorWeeklyEntitlement !== undefined, {
    message: 'Nothing to change',
  });
export type RemittanceSettingsInput = z.infer<typeof remittanceSettingsSchema>;

/**
 * Charge one rider for a week before it is posted, without a payment.
 *
 * With no amount, the week is priced as posting would price it. With an
 * amount that differs from that — or for a rider the rules would not charge —
 * a reason is required, and the figure is kept as set by hand.
 */
export const remittanceChargeInputSchema = z.object({
  riderId: uuidSchema,
  startsOn: isoDateSchema,
  amount: positiveMoneySchema.optional(),
  reason: z.string().trim().min(3).max(500).optional(),
});
export type RemittanceChargeInput = z.infer<typeof remittanceChargeInputSchema>;

/** Give one rider's week its own due date, with a reason. */
export const remittanceDueDateSchema = z.object({
  dueOn: isoDateSchema,
  reason: z.string().trim().min(3).max(500),
});
export type RemittanceDueDateInput = z.infer<typeof remittanceDueDateSchema>;

// ── Clearing what a rider owes ─────────────────────────────────────────────

/**
 * Forgive part or all of a rider's outstanding remittance.
 *
 * A write-off, never a payment: nothing is received, nothing is revenue, the
 * charge is unchanged. The amount comes off the chosen weeks oldest first;
 * with no weeks chosen, off every week the rider still owes on.
 */
export const remittanceDebtClearanceSchema = z
  .object({
    /** The weeks to clear from. Omitted: every week still owed. */
    remittanceIds: z.array(uuidSchema).min(1).max(200).optional(),
    /** How much to clear. Omitted with `clearAll`: everything outstanding on those weeks. */
    amount: positiveMoneySchema.optional(),
    clearAll: z.boolean().optional(),
    reason: z.string().trim().min(3).max(500),
    note: longTextSchema.optional(),
    /** One per submission, so a double-click clears once. */
    idempotencyKey: z.string().trim().min(8).max(100),
  })
  .refine((v) => v.clearAll === true || v.amount !== undefined, {
    message: 'Give an amount, or clear the whole balance',
    path: ['amount'],
  });
export type RemittanceDebtClearanceInput = z.infer<typeof remittanceDebtClearanceSchema>;

export const riderDebtWeekSchema = z.object({
  remittanceId: uuidSchema,
  startsOn: z.string(),
  endsOn: z.string(),
  label: z.string(),
  amountDue: moneySchema,
  paid: moneySchema,
  cleared: moneySchema,
  remaining: moneySchema,
});

export const remittanceDebtClearanceViewSchema = z.object({
  id: uuidSchema,
  amount: moneySchema,
  outstandingBefore: moneySchema,
  outstandingAfter: moneySchema,
  reason: z.string(),
  note: z.string().nullable(),
  recordedBy: z.string().nullable(),
  createdAt: z.string(),
  weeks: z.array(z.object({ remittanceId: uuidSchema, startsOn: z.string(), endsOn: z.string(), amount: moneySchema })),
});
export type RemittanceDebtClearanceView = z.infer<typeof remittanceDebtClearanceViewSchema>;

export const riderDebtSchema = z.object({
  riderId: uuidSchema,
  riderName: z.string(),
  externalId: z.string().nullable(),
  currencyCode: z.string(),
  outstanding: moneySchema,
  weeks: z.array(riderDebtWeekSchema),
  clearances: z.array(remittanceDebtClearanceViewSchema),
});
export type RiderDebt = z.infer<typeof riderDebtSchema>;
